import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { Spinner } from "../components/ui";
import { isAuthenticated } from "../utils/tokenUtils";
import { buildDeviceLinkAuthRedirect, DEVICE_LINK_PATH } from "../utils/nextPath";
import { applyLangParam } from "../utils/langParam";
import { trackDeviceLinkAuthRequired } from "../analytics/funnelAnalytics";
import { FORCE_WIZARD_PARAM, resolveDeviceLinkDestination } from "../features/devices/linkDestination";

/** ¿Llegó de la landing (o de otra campaña) y no pidió quedarse en el paso a paso? */
export const arrivesFromCampaign = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.has("channel") && params.get(FORCE_WIZARD_PARAM) !== "1";
};

/**
 * Entrada a `/dispositivos/vincular` (CC-CONSOLE#1561).
 *
 * - Sin sesión: vincular exige una cuenta (el equipo queda asignado a
 *   alguien). Se manda al alta gratis con el aviso de vincular y
 *   `next=/dispositivos/vincular`, conservando la procedencia (`channel`,
 *   `utm_*`, `ref`, `ph_did`/`ph_sid`) y el idioma.
 * - Con sesión y llegando de la landing (`?channel=`): si la compañía ya tiene
 *   equipos reales, a `/admin/devices?vincular=1`; si no, el paso a paso.
 *   `?forzar=1` se queda en el paso a paso.
 * - Con sesión desde la consola (bienvenida, menú, Sandbox): sin cambios.
 */
export default function DeviceLinkAuthGate() {
  const { search } = useLocation();
  const authed = isAuthenticated();
  const checkCount = authed && arrivesFromCampaign(search);
  const [target, setTarget] = useState<string | null>(null);
  const [checked, setChecked] = useState(!checkCount);

  useEffect(() => {
    applyLangParam(search);
  }, [search]);

  useEffect(() => {
    if (!authed) trackDeviceLinkAuthRequired({ hasAccountHint: false });
  }, [authed]);

  useEffect(() => {
    if (!checkCount) return;
    let active = true;
    const lang = new URLSearchParams(search).get("lang");
    void resolveDeviceLinkDestination({ wizardPath: `${DEVICE_LINK_PATH}${search}`, lang }).then((dest) => {
      if (!active) return;
      if (!dest.startsWith(DEVICE_LINK_PATH)) setTarget(dest);
      setChecked(true);
    });
    return () => {
      active = false;
    };
  }, [checkCount, search]);

  if (!authed) return <Navigate to={buildDeviceLinkAuthRedirect(search)} replace />;
  if (target) return <Navigate to={target} replace />;
  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app" data-testid="link-gate-loading">
        <Spinner />
      </div>
    );
  }
  return <Outlet />;
}
